/**
 * Single favourite-vehicle card.
 *
 * Renders ONLY the public StockListItem fields (image, title, year, mileage,
 * grade, price) plus the favourite (♡) toggle. The public Stock browse (list +
 * detail) was removed, so its only consumer is the Favorites list
 * (/stock/favorites). Optionally the card:
 *   - deep-links to the AUCNET Stock front detail page from a dedicated action.
 *     The action and favourite toggle are siblings, so the controls remain
 *     independently usable and can wrap inside narrow cards.
 *   - renders a SOLD treatment (`sold` — dimmed image + SOLD ribbon, no link)
 *     while keeping the remove button usable.
 */
import { AisCheckBadge } from './AisCheckBadge';
import { FavoriteToggleButton } from './FavoriteToggleButton';
import { StockImage } from './StockImage';
import { formatJpy, formatMileageKm, formatRegistration } from '../../lib/stockApi';
import { BRAND } from '../layout/designTokens';
import type { StockListItem } from '../../lib/stockTypes';

interface Props {
  item: StockListItem;
  onFavoriteChanged?: (vehicleId: string, favorited: boolean) => void;
  /** AS front detail URL. When set (and not `sold`) the card becomes a link. */
  href?: string | null;
  /** SOLD treatment: overlay on the image, never a link. */
  sold?: boolean;
}

export function StockListCard({ item, onFavoriteChanged, href, sold }: Props): JSX.Element {
  const title = [item.maker, item.carname].filter(Boolean).join(' ') || '—';
  const subtitle = item.grade ?? '';
  const linked = Boolean(href) && !sold;
  return (
    <article
      data-testid={`stock-card-${item.vehicleId}`}
      aria-label={sold ? `${title} — SOLD` : undefined}
      style={{
        border: '1px solid #ddd',
        borderRadius: '0.5rem',
        background: '#fff',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div>
        <div
          style={{
            position: 'relative',
            aspectRatio: '4 / 3',
            background: '#f4f4f4',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#999',
            fontSize: '0.85rem',
          }}
        >
          {item.thumbnailUrl ? (
            <StockImage
              src={item.thumbnailUrl}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <span>No image</span>
          )}
          {sold ? (
            <div
              data-testid={`stock-card-sold-${item.vehicleId}`}
              aria-hidden="true"
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: `${BRAND.navy900}B3`,
                color: BRAND.white,
                fontSize: '1.25rem',
                fontWeight: 700,
                letterSpacing: '0.08em',
              }}
            >
              SOLD
            </div>
          ) : null}
        </div>
        <div style={{ padding: '0.75rem' }}>
          <h2
            style={{
              fontSize: '1rem',
              margin: 0,
              fontWeight: 600,
              lineHeight: 1.3,
            }}
          >
            {title}
          </h2>
          {subtitle ? (
            <p
              style={{
                fontSize: '0.85rem',
                color: '#666',
                margin: '0.25rem 0 0',
              }}
            >
              {subtitle}
            </p>
          ) : null}
          {item.inspectionStatus === 'aiscore_done' ? (
            <div data-testid={`stock-card-ais-${item.vehicleId}`} style={{ margin: '0.5rem 0 0' }}>
              <AisCheckBadge size={18} showLabel />
            </div>
          ) : null}
          <dl
            style={{
              display: 'grid',
              gridTemplateColumns: 'auto 1fr',
              gap: '0.25rem 0.75rem',
              margin: '0.75rem 0 0',
              fontSize: '0.85rem',
            }}
          >
            <dt style={{ color: '#666' }}>Year</dt>
            <dd style={{ margin: 0 }}>{formatRegistration(item.registration)}</dd>
            <dt style={{ color: '#666' }}>Mileage</dt>
            <dd style={{ margin: 0 }}>{formatMileageKm(item.mileage)}</dd>
            {item.auctionGrade ? (
              <>
                <dt style={{ color: '#666' }}>Grade</dt>
                <dd style={{ margin: 0 }}>{item.auctionGrade}</dd>
              </>
            ) : null}
          </dl>
          <p
            style={{
              fontSize: '1.15rem',
              fontWeight: 700,
              color: '#1f6feb',
              margin: '0.75rem 0 0',
            }}
            data-testid={`stock-card-price-${item.vehicleId}`}
          >
            {formatJpy(item.listPrice)}
          </p>
        </div>
      </div>
      <div
        style={{
          padding: '0 0.75rem 0.75rem',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'stretch',
          gap: '0.5rem',
        }}
      >
        {linked ? (
          <a
            href={href as string}
            data-testid={`stock-card-link-${item.vehicleId}`}
            aria-label={`View ${title}`}
            style={{
              flex: '1 1 9rem',
              minWidth: 0,
              minHeight: '2.75rem',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid #1f6feb',
              borderRadius: 9999,
              color: '#1f6feb',
              background: '#fff',
              fontSize: '0.875rem',
              fontWeight: 600,
              textDecoration: 'none',
              whiteSpace: 'nowrap',
              padding: '0.5rem 0.75rem',
              boxSizing: 'border-box',
            }}
          >
            View vehicle →
          </a>
        ) : null}
        <div style={{ flex: '1 1 9rem', minWidth: 0, display: 'flex' }}>
          <FavoriteToggleButton
            vehicleId={item.vehicleId}
            initialFavorited={item.isFavorited}
            onChanged={(favorited) => onFavoriteChanged?.(item.vehicleId, favorited)}
          />
        </div>
      </div>
    </article>
  );
}
